import { computed, DestroyRef, Injectable, inject, type Signal, signal } from '@angular/core';
import type { MmscanNavItem } from '@mmscan/angular-ui/navigation';

export interface ShellSection {
  heading: string;
  items: readonly MmscanNavItem[];
}

/**
 * The section navigation (Access Control, MiCare, Vehicle Catalog…) the app shell shows in its
 * sidebar. A section's host component owns its items and permissions and hands them over with
 * `use()`; the sidebar goes away when that host is destroyed.
 */
@Injectable({ providedIn: 'root' })
export class ShellSidebarService {
  private readonly source = signal<Signal<ShellSection> | null>(null);

  /** The active section, following the host's signal (permissions arriving, language switches). */
  readonly section = computed(() => this.source()?.() ?? null);

  /** Shows `section` while the calling component lives. Call from an injection context. */
  use(section: Signal<ShellSection>): void {
    this.source.set(section);
    inject(DestroyRef).onDestroy(() => {
      // Navigating between sections creates the next host before destroying this one.
      if (this.source() === section) this.source.set(null);
    });
  }
}
